import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { deAuthenticate } from "../../store/reducers/authSlice";
import { clearPersistedData } from "../../store/configureStore";
import "./style.scss";

// Import Icons
import Risk from "../../assets/icons/riskmangement.svg";
import RiskActive from "../../assets/icons/riskmangementActive.svg";
import BrandRiskActive from "../../assets/icons/BrandriskmangementActive.svg";
import TradersList from "../../assets/icons/traderslist.svg";
import TradersListActive from "../../assets/icons/traderslist-active.svg";
import BrandTradersListActive from "../../assets/icons/Brandtraderslist-active.svg";
import UserList from "../../assets/icons/userlist.svg";
import UserListActive from "../../assets/icons/userlist-active.svg";
import BrandUserListActive from "../../assets/icons/Branduserlist-active.svg";
import Payouts from "../../assets/icons/payouts.svg";
import PayoutsActive from "../../assets/icons/payouts-active.svg";
import BrandPayoutsActive from "../../assets/icons/Brandpayouts-active.svg";
import Payment from "../../assets/icons/payment.svg";
import PaymentActive from "../../assets/icons/paymentActive.svg";
import BrandPaymentActive from "../../assets/icons/BrandpaymentActive.svg";
import Logout from "../../assets/icons/logout.svg";
import Support from "../../assets/icons/support.svg";
import StageManager from "../../assets/icons/StageManager.svg";
import StageManagerActive from "../../assets/icons/Stage_Manager_Active.svg";
import BrandStageManagerActive from "../../assets/icons/BrandStage_Manager_Active.svg";
import SupportActive from "../../assets/icons/supportActive.svg";
import BrandSupportActive from "../../assets/icons/BrandsupportActive.svg";
import CrossIcon from "../../assets/icons/cross-gray.svg";
import Certificates from "../../assets/icons/Certificates.svg";
import Certificates_Active from "../../assets/icons/Certificates_Active.svg";
import BrandCertificates_Active from "../../assets/icons/BrandCertificates_Active.svg";
import Coupons from "../../assets/icons/coupons.svg";
import CouponsActive from "../../assets/icons/coupons-active.svg";
import BrandCouponsActive from "../../assets/icons/Brandcoupons-active.svg";
import AccountCreate from "../../assets/icons/AccountCreate.svg";
import AccountCreateActive from "../../assets/icons/AccountCreateActive.svg";
import BrandAccountCreateActive from "../../assets/icons/BrandAccountCreateActive.svg";
import Affiliate from "../../assets/icons/affiliate.svg";
import AffiliateActive from "../../assets/icons/affiliate-active.svg";
import BrandAffiliateActive from "../../assets/icons/Brandaffiliate-active.svg";
import ip_log from "../../assets/icons/iplog.svg";
import ip_logActive from "../../assets/icons/iplogActive.svg";
import Brandip_logActive from "../../assets/icons/BrandiplogActive.svg";
import { PURGE_Brand } from "../../store/reducers/brandReducer";

// Sidebar Component
function Sidebar({
  headerName,
  zIndexValue,
  setZIndexValue,
  mobileMenu,
  setMobileMenu,
}) {
  const [isSideMenuOpen, setSideMenuOpen] = useState(false);
  const sidebarRef = useRef(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { custom_permissions } = useSelector((state) => state.auth);
  const { selectedBrand } = useSelector((state) => state.brand);

  const sidebarList = [
    {
      title: "Trader List",
      path: "/super-trader-list/",
      icon: TradersList,
      activeIcon: TradersListActive,
      brandIcon: BrandTradersListActive,
      adminAccess: custom_permissions?.includes("trader_list"),
    },
    ...(selectedBrand?.brand === "equity_edge"
      ? [
          {
            title: "Risk Management",
            path: "/risk-management",
            icon: Risk,
            activeIcon: RiskActive,
            brandIcon: BrandRiskActive,
            adminAccess: custom_permissions?.includes("risk_management"),
          },
        ]
      : []),
    {
      title: "Stage Manager",
      path: "/stage-management",
      icon: StageManager,
      activeIcon: StageManagerActive,
      brandIcon: BrandStageManagerActive,
      adminAccess: custom_permissions?.includes("stage_manager"),
    },
    {
      title: "User List",
      path: "/user-list",
      icon: UserList,
      activeIcon: UserListActive,
      brandIcon: BrandUserListActive,
      adminAccess: custom_permissions?.includes("user_management"),
    },
    {
      title: "Create Account",
      path: "/funding-evaluation",
      icon: AccountCreate,
      activeIcon: AccountCreateActive,
      brandIcon: BrandAccountCreateActive,
      adminAccess: custom_permissions?.includes("account_creation"),
    },
    {
      title: "Certificates",
      path: "/CertificateManagement",
      icon: Certificates,
      activeIcon: Certificates_Active,
      brandIcon: BrandCertificates_Active,
      adminAccess: custom_permissions?.includes("certificates"),
      submenu: true,
      items: [
        { title: "All Certificates", path: "/CertificateManagement", tab: 0 },

        {
          title: "Create Certificate",
          path: "/CertificateManagement/create-certificate",
          tab: 1,
        },
      ],
    },
    {
      title: "Payments",
      path: "/paymentlist",
      icon: Payouts,
      activeIcon: PayoutsActive,
      brandIcon: BrandPayoutsActive,
      adminAccess: custom_permissions?.includes("payments"),
    },
    {
      title: "Payout",
      path: "/payout_list",
      icon: Payment,
      activeIcon: PaymentActive,
      brandIcon: BrandPaymentActive,
      adminAccess: custom_permissions?.includes("payout"),
    },
    {
      adminAccess: custom_permissions?.includes("coupons"),
      title: "Coupons",
      path: "/coupons",
      icon: Coupons,
      activeIcon: CouponsActive,
      brandIcon: BrandCouponsActive,
      submenu: true,
      items: [
        { title: "All Coupons", path: "/coupons" },
        {
          title: "Create Coupons",
          path: "/coupons/create-coupon",
        },
      ],
    },
    {
      title: "Affiliate Market",
      path: "/affiliate-marketing",
      icon: Affiliate,
      activeIcon: AffiliateActive,
      brandIcon: BrandAffiliateActive,
      adminAccess: custom_permissions?.includes("affiliate_market"),
      submenu: true,
      items: [
        {
          title: "Affiliates list",
          path: "/affiliate-marketing/affiliate-list",
        },
        { title: "Codes List", path: "/affiliate-marketing/codes-list" },
        {
          title: "Create Affiliate Code",
          path: "/affiliate-marketing/create-codes",
        },
      ],
    },
    {
      title: "Admin Support",
      path: "/superadmin",
      icon: Support,
      activeIcon: SupportActive,
      brandIcon: BrandSupportActive,
      adminAccess: custom_permissions?.includes("admin_support"),
    },
    {
      title: "Reports",
      path: "/reports",
      icon: Support,
      activeIcon: SupportActive,
      brandIcon: BrandSupportActive,
      adminAccess: custom_permissions?.includes("risk_management"),
    },
    // {
    //   title: "User Reports",
    //   path: "/user-reports",
    //   icon: UserList,
    //   activeIcon: UserListActive,
    //   brandIcon: BrandUserListActive,
    //   adminAccess: custom_permissions?.includes("user_management"),
    // },
    {
      title: "IP Log",
      path: "/ip-log",
      icon: ip_log,
      activeIcon: ip_logActive,
      brandIcon: Brandip_logActive,
      adminAccess: custom_permissions?.includes("ip_log"),
    },
    {
      title: "Notification",
      path: "/notification",
      icon: ip_log,
      activeIcon: ip_logActive,
      brandIcon: Brandip_logActive,
      adminAccess: custom_permissions?.includes("notification") || true,
    },
  ];

  const handleLogout = () => {
    clearPersistedData();
    dispatch(deAuthenticate());
    dispatch(PURGE_Brand());
    window.location.reload();
  };

  const closeSideMenu = () => {
    setSideMenuOpen(false);
    setZIndexValue(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        closeSideMenu();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <div className="sidebar" ref={sidebarRef}>
        <ul className="icons_list">
          {sidebarList.map((item, index) => {
            const isActive = location.pathname.startsWith(item.path);
            if (item.adminAccess) {
              return (
                <li className={`icon ${isActive ? "active" : ""}`} key={index}>
                  <Link to={item.path} onClick={closeSideMenu}>
                    <img
                      src={
                        isActive
                          ? selectedBrand?.brand === "crypto_edge"
                            ? item.brandIcon
                            : item.activeIcon
                          : item.icon
                      }
                      alt={item.title}
                    />
                    {item.title}
                  </Link>
                  {item.submenu && (
                    <SubMenu items={item.items} show={isActive} />
                  )}
                </li>
              );
            }
            return null;
          })}
          <li className="icon">
            <Link to="/signin" onClick={handleLogout}>
              <img src={Logout} alt="logout" />
              Logout
            </Link>
          </li>
        </ul>
      </div>
      {mobileMenu && (
        <MobileMenu
          mobileMenu={mobileMenu}
          setMobileMenu={setMobileMenu}
          sidebarList={sidebarList}
          handleLogout={handleLogout}
          selectedBrand={selectedBrand}
        />
      )}
    </>
  );
}

const SubMenu = ({ items, show }) => {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <ul className={`submenu ${show ? "show" : ""}`}>
      {items.map((item) => (
        <li
          key={item.title}
          className={location.pathname === item.path ? "active" : ""}
        >
          <Link to={item.path} onClick={() => navigate(item.path)}>
            {item.title}
          </Link>
        </li>
      ))}
    </ul>
  );
};

const MobileMenu = ({
  mobileMenu,
  setMobileMenu,
  sidebarList,
  handleLogout,
  selectedBrand,
}) => {
  const location = useLocation(); // Get the current location/pathname

  return (
    <div className={`mobile-menu ${mobileMenu ? "show" : ""}`}>
      <div className="menu_box">
        <div className="close_button" onClick={() => setMobileMenu(false)}>
          <img src={CrossIcon} alt="close icon" />
        </div>
        <ul className="mobile-icons_list">
          {sidebarList.map((item, index) => {
            if (!item.adminAccess) return null; // Skip if no access
            const isActive = location.pathname.startsWith(item.path); // Check if current path matches

            return (
              <li key={index} className={isActive ? "activeMobileMenu" : ""}>
                <Link to={item.path} onClick={() => setMobileMenu(false)}>
                  <img
                    src={
                      isActive
                        ? selectedBrand?.brand === "equity_edge"
                          ? item.activeIcon
                          : item?.brandIcon
                        : item.icon
                    }
                    alt={item.title}
                  />
                  {item.title}
                </Link>
                {item.submenu && <SubMenu items={item.items} />}
              </li>
            );
          })}
          <li>
            <Link to="/signin" onClick={handleLogout}>
              <img src={Logout} alt="logout" />
              Logout
            </Link>
          </li>
        </ul>
      </div>
    </div>
  );
};
export default Sidebar;
